import React from 'react';
import { Link } from 'react-router-dom';
import { usePageMeta, organizationSchema, serviceSchema, faqSchema, breadcrumbSchema } from '../lib/seo';
import { siteConfig } from '../siteConfig';
import {
  aiReceptionistFaqs,
  chatbotFaqs,
  websitesFaqs,
  pricingFaqs,
  offers,
  industries,
  demoSteps,
  portfolio,
} from '../data/siteData';
import {
  AnswerBlock,
  ButtonLink,
  Card,
  Container,
  CTASection,
  DemoCallout,
  FAQList,
  FeatureList,
  PageHero,
  PriceCard,
  Section,
  SectionHeading,
} from '../components/SiteUi';

const serviceJsonLd = ({ name, serviceType, path, description }) => [
  organizationSchema(),
  serviceSchema({ name, serviceType, path, description }),
  breadcrumbSchema([{ name: 'Home', path: '/' }, { name, path }]),
];

export function AiReceptionistPage() {
  const faqs = aiReceptionistFaqs;
  usePageMeta({
    title: 'AI Receptionist for Australian Trades – CloudNext',
    description: 'Answer every call, after hours and on site. CloudNext sets up and manages a voice AI receptionist that captures details and books jobs.',
    path: '/ai-receptionist',
    jsonLd: [...serviceJsonLd({ name: 'AI Receptionist', serviceType: 'AI phone answering and appointment booking', path: '/ai-receptionist', description: 'Voice AI receptionist we set up and run for Australian trades and service businesses.' }), faqSchema(faqs)],
  });

  return (
    <>
      <PageHero
        eyebrow="AI Receptionist"
        title={<>Answer every call. <span className="text-gold-gradient">Book more jobs.</span></>}
        description="A voice AI receptionist that answers calls, captures the details you need and books appointments. CloudNext sets it up, tests it and manages it for you."
        breadcrumbs={[{ name: 'Home', to: '/' }, { name: 'AI Receptionist' }]}
      >
        <div className="flex flex-col gap-3 sm:flex-row">
          <ButtonLink to="/demo">Hear the live demo</ButtonLink>
          <ButtonLink to="/pricing" variant="secondary">See pricing</ButtonLink>
        </div>
      </PageHero>

      <Section>
        <Container>
          <div className="grid gap-10 lg:grid-cols-[1.2fr_0.8fr] lg:items-start">
            <div>
              <SectionHeading eyebrow="What it is." title="What is an AI receptionist?" />
              <AnswerBlock className="mt-5">
                An AI receptionist is a voice agent that answers phone calls, answers common questions, captures the caller's details and books appointments. It works after hours and during busy periods, then sends a summary to the business. It is different from a website chatbot, which handles text chat on a website.
              </AnswerBlock>
              <p className="mt-5 text-sm leading-6 text-dust">
                For a trade business, the value is simple: the call is answered while you are on the tools, driving or closed. The caller gets a next step instead of voicemail.
              </p>
            </div>
            <Card>
              <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-gold-light">Good for</p>
              <FeatureList
                className="mt-4"
                items={[
                  'Plumbers, electricians and HVAC',
                  'Mowing, cleaning and home services',
                  'Businesses that miss calls during the day',
                  'Businesses that get after-hours enquiries',
                  'Teams that want it set up and managed',
                ]}
              />
            </Card>
          </div>
        </Container>
      </Section>

      <Section className="border-y border-white/[0.04] bg-ink-light/40">
        <Container>
          <SectionHeading eyebrow="What it handles" title="The calls that usually go to voicemail." />
          <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[
              { title: 'New enquiries', text: 'Capture the caller, the job and the best time to call back.' },
              { title: 'Bookings', text: 'Offer available times and write the booking into your calendar or job system.' },
              { title: 'Reschedules', text: 'Move or cancel an appointment using the rules you approve.' },
              { title: 'Common questions', text: 'Answer from your services, hours, service area and FAQs.' },
              { title: 'After hours', text: 'Answer when the business is closed, with a clear next step.' },
              { title: 'When you\'re busy', text: 'Pick up when the team is busy, on another call or with a customer.' },
              { title: 'Urgent calls', text: 'Pass urgent calls to a person using your rules, without giving technical or safety advice.' },
              { title: 'Passing calls to a person', text: 'Transfer to a person or take a structured message when needed.' },
            ].map((item) => (
              <Card key={item.title}>
                <h3 className="font-display text-lg text-cream">{item.title}</h3>
                <p className="mt-2 text-sm leading-6 text-dust">{item.text}</p>
              </Card>
            ))}
          </div>
        </Container>
      </Section>

      <Section>
        <Container>
          <div className="grid gap-10 lg:grid-cols-2">
            <div>
              <SectionHeading eyebrow="How it works" title="Set up, tested, then managed." />
              <div className="mt-8 space-y-4">
                {[
                  'We map your services, hours, service area, pricing rules and FAQs.',
                  'We configure how calls are handled, calendar, booking rules and what happens when it can’t answer.',
                  'We test normal, after-hours, emergency, bad-line and calls passed to a person.',
                  'We go live, monitor the first weeks and tune the flow.',
                ].map((step, index) => (
                  <div key={step} className="flex gap-4 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5">
                    <span className="font-mono-accent text-sm text-gold-light">0{index + 1}</span>
                    <p className="text-sm leading-6 text-dust">{step}</p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <SectionHeading eyebrow="Integrations" title="It has to land in the system you already use." description="The exact connections depend on the setup. We confirm what is live before you sign." />
              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                {['Google Calendar', 'Outlook Calendar', 'ServiceM8', 'Tradify', 'Fergus', 'Xero', 'Stripe', 'Custom API or webhook'].map((item) => (
                  <div key={item} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] px-4 py-3 text-sm text-dust">{item}</div>
                ))}
              </div>
              <div className="mt-5">
                <ButtonLink to="/integrations" variant="secondary" size="sm">See how connections work</ButtonLink>
              </div>
            </div>
          </div>
        </Container>
      </Section>

      <Section className="border-y border-white/[0.04] bg-ink-light/40">
        <Container>
          <SectionHeading eyebrow="Trades" title="Built for the way your trade works." />
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {industries.map((industry) => (
              <Link key={industry.slug} to={`/industries/${industry.slug}`} className="group rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 transition hover:-translate-y-0.5 hover:border-gold/40">
                <h3 className="font-display text-lg text-cream group-hover:text-gold-light">{industry.name}</h3>
                <p className="mt-2 text-sm leading-6 text-dust">{industry.intro}</p>
                <span className="mt-4 inline-block text-sm text-gold-light">See the setup →</span>
              </Link>
            ))}
          </div>
        </Container>
      </Section>

      <Section>
        <Container>
          <SectionHeading
            eyebrow="Pricing"
            title="The AI receptionist comes in the full bundle."
            description="We do not sell the AI receptionist on its own. It is included in Website + Chatbot + AI Receptionist, with 300 or 1,000 minutes."
          />
          <div className="mt-10 max-w-xl">
            <PriceCard offer={offers.find((offer) => offer.key === 'websiteChatbotVoice')} featured />
          </div>
          <div className="mt-8">
            <ButtonLink to="/pricing" variant="secondary">See the full pricing guide</ButtonLink>
          </div>
        </Container>
      </Section>

      <Section className="border-y border-white/[0.04] bg-ink-light/40">
        <Container>
          <DemoCallout />
        </Container>
      </Section>

      <Section>
        <Container>
          <SectionHeading eyebrow="Questions" title="The things to ask before you buy." />
          <div className="mt-8">
            <FAQList items={faqs} />
          </div>
        </Container>
      </Section>

      <CTASection
        title="Want to see it handle your calls?"
        description="Book a 15-minute call and we will walk through how your calls are handled, your calendar and booking rules."
        primaryTo="/contact"
        primaryLabel="Book a call"
        secondaryTo="/demo"
        secondaryLabel="What to try on the demo"
      />
    </>
  );
}

export function WebsiteChatbotPage() {
  usePageMeta({
    title: 'Website Chatbot for Trades – CloudNext',
    description: 'Collect enquiries and book appointments from your website 24/7. CloudNext sets up, trains and manages your chatbot.',
    path: '/website-chatbot',
    jsonLd: [...serviceJsonLd({ name: 'Website Chatbot', serviceType: 'Website chatbot setup, training and management', path: '/website-chatbot', description: 'Website chatbot we set up and run for Australian trades and service businesses.' }), faqSchema(chatbotFaqs)],
  });

  return (
    <>
      <PageHero
        eyebrow="Website Chatbot"
        title={<>Turn website visitors into <span className="text-gold-gradient">real enquiries.</span></>}
        description="A chatbot trained on your business that answers questions, captures contact details and starts booking requests. CloudNext sets it up and manages it for you."
        breadcrumbs={[{ name: 'Home', to: '/' }, { name: 'Website Chatbot' }]}
      >
        <div className="flex flex-col gap-3 sm:flex-row">
          <ButtonLink to="/contact">Ask about a setup</ButtonLink>
          <ButtonLink to="/pricing" variant="secondary">See pricing</ButtonLink>
        </div>
      </PageHero>

      <Section>
        <Container>
          <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr]">
            <div>
              <SectionHeading eyebrow="What it does" title="A useful first conversation, not a pop-up." />
              <FeatureList
                className="mt-6"
                items={[
                  'Answers common questions from your approved information',
                  'Captures name, phone, email and the job details',
                  'Starts a booking request and routes it to you',
                  'Works after hours and during busy periods',
                  'Can hand over to your team or take a message',
                ]}
              />
              <p className="mt-6 text-sm leading-6 text-dust">
                The chatbot is powered by BulletChat, the self-service platform. CloudNext sets it up for you: we train it, install it, tune it and manage it.
              </p>
            </div>
            <Card>
              <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-gold-light">Where it helps</p>
              <FeatureList
                className="mt-4"
                items={[
                  'Trades with a website but few enquiries',
                  'Businesses that get the same questions all day',
                  'Teams that cannot reply instantly',
                  'Businesses that want enquiries taken after hours',
                  'Any site that needs a clear next step',
                ]}
              />
            </Card>
          </div>
        </Container>
      </Section>

      <Section className="border-y border-white/[0.04] bg-ink-light/40">
        <Container>
          <SectionHeading eyebrow="How it works" title="Trained on your business, approved by you." />
          <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[
              { title: 'We collect', text: 'Services, hours, service area, FAQs, pricing rules and booking rules.' },
              { title: 'We train', text: 'The chatbot is trained on the approved content and tested against real questions.' },
              { title: 'We install', text: 'It goes on your site and collects enquiries, with calls passed to a person the way you want.' },
              { title: 'We tune', text: 'We review the conversations and improve the answers and flow.' },
            ].map((item) => (
              <Card key={item.title}>
                <h3 className="font-display text-lg text-cream">{item.title}</h3>
                <p className="mt-2 text-sm leading-6 text-dust">{item.text}</p>
              </Card>
            ))}
          </div>
        </Container>
      </Section>

      <Section>
        <Container>
          <SectionHeading eyebrow="Pricing" title="Start with the chatbot bundle, or add voice." />
          <div className="mt-10 grid gap-5 md:grid-cols-2">
            {offers.map((offer) => (
              <PriceCard key={offer.key} offer={offer} featured={offer.key === 'websiteChatbotVoice'} />
            ))}
          </div>
        </Container>
      </Section>

      <Section className="border-y border-white/[0.04] bg-ink-light/40">
        <Container>
          <SectionHeading eyebrow="Questions" title="What business owners ask." />
          <div className="mt-8"><FAQList items={chatbotFaqs} /></div>
        </Container>
      </Section>

      <CTASection
        title="Want a chatbot that actually captures the lead?"
        description="Tell us what your website gets asked, and we will tell you whether a chatbot is worth it."
        primaryTo="/contact"
        primaryLabel="Ask about a chatbot"
        secondaryTo="/ai-receptionist"
        secondaryLabel="See the AI receptionist"
      />
    </>
  );
}

export function WebsitesPage() {
  const demoPortfolio = portfolio.filter((item) => item.type === 'Demo website').slice(0, 3);
  usePageMeta({
    title: 'Websites for Trades That Turn Visitors Into Jobs – CloudNext',
    description: 'Websites built to get enquiries for plumbers, electricians and service businesses, with booking and optional AI chat.',
    path: '/websites',
    jsonLd: [...serviceJsonLd({ name: 'Website Design and Development', serviceType: 'Website design and development for trades and service businesses', path: '/websites', description: 'Websites built to get enquiries for Australian trades and service businesses.' }), faqSchema(websitesFaqs)],
  });

  return (
    <>
      <PageHero
        eyebrow="Websites"
        title={<>A website that turns visitors into <span className="text-gold-gradient">jobs.</span></>}
        description="Clear services, strong proof, fast mobile pages and a next step that is easy to take. Built for trades and service businesses, with optional chatbot and booking."
        breadcrumbs={[{ name: 'Home', to: '/' }, { name: 'Websites' }]}
      >
        <div className="flex flex-col gap-3 sm:flex-row">
          <ButtonLink to="/contact">See the website bundle</ButtonLink>
          <ButtonLink to="/work" variant="secondary">See the work</ButtonLink>
        </div>
      </PageHero>

      <Section>
        <Container>
          <div className="grid gap-10 lg:grid-cols-2">
            <div>
              <SectionHeading eyebrow="The problem" title="A website can look fine and still lose work." />
              <FeatureList
                className="mt-6"
                items={[
                  'The phone number is hard to find on mobile.',
                  'The services are vague and the service area is unclear.',
                  'There is no proof, no next step and no follow-up.',
                  'The site is slow, or it breaks on a phone.',
                  'Enquiries arrive by email and sit there.',
                ]}
              />
            </div>
            <Card>
              <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-gold-light">What is included</p>
              <FeatureList
                className="mt-4"
                items={[
                  'Mobile-first design and build',
                  'Service and service-area pages',
                  'Enquiry forms and tracking of enquiries',
                  'Examples of our work',
                  'Optional chatbot and booking',
                  'Set up so Google can find you',
                ]}
              />
            </Card>
          </div>
        </Container>
      </Section>

      <Section className="border-y border-white/[0.04] bg-ink-light/40">
        <Container>
          <SectionHeading eyebrow="Examples" title="Demo websites built for trades." description="These are demo sites, not client work." />
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {demoPortfolio.map((site) => (
              <a key={site.url} href={site.url} target="_blank" rel="noopener noreferrer" className="group overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.02] transition hover:-translate-y-0.5 hover:border-gold/40">
                <img src={site.image} alt={`${site.title} website`} className="h-44 w-full object-cover object-top" loading="lazy" />
                <div className="p-5">
                  <h3 className="font-display text-lg text-cream group-hover:text-gold-light">{site.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-dust">{site.description}</p>
                  <span className="mt-4 inline-block text-sm text-gold-light">View demo →</span>
                </div>
              </a>
            ))}
          </div>
        </Container>
      </Section>

      <Section>
        <Container>
          <SectionHeading
            eyebrow="Pricing"
            title="Websites come as part of the lead-capture bundle."
            description="We do not publish a website-only price. Choose Website + Chatbot, or add the AI receptionist."
          />
          <div className="mt-10 grid gap-5 md:grid-cols-2">
            {offers.map((offer) => (
              <PriceCard key={offer.key} offer={offer} featured={offer.key === 'websiteChatbotVoice'} />
            ))}
          </div>
        </Container>
      </Section>

      <Section className="border-y border-white/[0.04] bg-ink-light/40">
        <Container>
          <SectionHeading eyebrow="Questions" title="What business owners ask." />
          <div className="mt-8"><FAQList items={websitesFaqs} /></div>
        </Container>
      </Section>

      <CTASection
        title="A website is not the whole job."
        description="If the site does not capture and follow up, it is a brochure. We can fix the whole path: site, chat, calls and booking."
        primaryTo="/contact"
        primaryLabel="See the website bundle"
        secondaryTo="/complete"
        secondaryLabel="See the complete setup"
      />
    </>
  );
}

export function CompletePage() {
  usePageMeta({
    title: 'Website, Chatbot and AI Receptionist, Managed – CloudNext',
    description: 'One team for the whole setup: website, chatbot, AI receptionist, booking and reporting.',
    path: '/complete',
    jsonLd: [...serviceJsonLd({ name: 'Complete Setup', serviceType: 'Website, chatbot and AI receptionist setup and management', path: '/complete', description: 'Website, chatbot and AI receptionist, set up and managed by CloudNext.' })],
  });

  return (
    <>
      <PageHero
        eyebrow="Complete Setup"
        title={<>One team for the <span className="text-gold-gradient">whole lead path.</span></>}
        description="Website, chatbot, AI receptionist, booking and reporting. Set up together, tested together and managed by one team."
        breadcrumbs={[{ name: 'Home', to: '/' }, { name: 'Complete Setup' }]}
      >
        <div className="flex flex-col gap-3 sm:flex-row">
          <ButtonLink to="/contact">Ask about the complete setup</ButtonLink>
          <ButtonLink to="/pricing" variant="secondary">See pricing</ButtonLink>
        </div>
      </PageHero>

      <Section>
        <Container>
          <SectionHeading eyebrow="What is included" title="The whole path, not one piece." />
          <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[
              { title: 'Website', text: 'A clear, fast site that gives visitors a next step.' },
              { title: 'Chatbot', text: 'Answers questions and captures leads from the site.' },
              { title: 'AI receptionist', text: 'Answers calls, captures details and books jobs.' },
              { title: 'Booking and reporting', text: 'Calendar, job-system connection, summaries and reporting.' },
            ].map((item) => (
              <Card key={item.title}>
                <h3 className="font-display text-lg text-cream">{item.title}</h3>
                <p className="mt-2 text-sm leading-6 text-dust">{item.text}</p>
              </Card>
            ))}
          </div>
          <div className="mt-8">
            <AnswerBlock>
              You approve one set of services, hours, pricing rules and FAQs, and the same rules apply across chat and phone.
            </AnswerBlock>
          </div>
        </Container>
      </Section>

      <Section className="border-y border-white/[0.04] bg-ink-light/40">
        <Container>
          <SectionHeading eyebrow="Good for" title="Who this is for." />
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {[
              { title: 'You are losing calls', text: 'The phone rings while you are on the tools, driving or closed.' },
              { title: 'Your site gets traffic', text: 'People visit, but few enquire or book.' },
              { title: 'You want one team', text: 'You do not want to stitch together a web designer, a chatbot tool and a phone system.' },
            ].map((item) => (
              <Card key={item.title}>
                <h3 className="font-display text-lg text-cream">{item.title}</h3>
                <p className="mt-2 text-sm leading-6 text-dust">{item.text}</p>
              </Card>
            ))}
          </div>
        </Container>
      </Section>

      <Section>
        <Container>
          <SectionHeading eyebrow="Pricing" title="The complete setup." />
          <div className="mt-10 max-w-xl">
            <PriceCard offer={offers.find((offer) => offer.key === 'websiteChatbotVoice')} featured />
          </div>
          <p className="mt-5 text-sm leading-6 text-ash">
            The final quote depends on pages, how calls are handled, minutes, connections and support. The call confirms the scope.
          </p>
        </Container>
      </Section>

      <CTASection
        title="Want the whole path handled?"
        description="We will tell you what to set up first, and what can wait. No pressure to buy all three."
        primaryTo="/contact"
        primaryLabel="Book a call"
        secondaryTo="/ai-receptionist"
        secondaryLabel="See the AI receptionist"
      />
    </>
  );
}

export function PricingPage() {
  usePageMeta({
    title: 'AI Receptionist and Website Pricing – CloudNext',
    description: 'Clear setup and monthly pricing for AI receptionists, websites and chatbots. See what is included and book a 15-minute call.',
    path: '/pricing',
    jsonLd: [...serviceJsonLd({ name: 'CloudNext Pricing', serviceType: 'AI receptionist, website and chatbot pricing', path: '/pricing', description: 'Special and standard pricing for CloudNext bundles, set up and run for you.' }), faqSchema(pricingFaqs)],
  });

  return (
    <>
      <PageHero
        eyebrow="Special pricing"
        title={<>50% off setup <span className="text-gold-gradient">until 31 December 2026.</span></>}
        description="Two bundles: Website + Chatbot, or Website + Chatbot + AI Receptionist. The setup is half price until 31 December 2026; standard setup applies from 1 January 2027."
        breadcrumbs={[{ name: 'Home', to: '/' }, { name: 'Pricing' }]}
      >
        <ButtonLink to="/contact">Book a call</ButtonLink>
      </PageHero>

      <Section>
        <Container>
          <SectionHeading
            eyebrow="Offers"
            title="Choose your bundle."
            description="Both bundles include the website, setup and ongoing support. We do not sell websites on their own."
          />
          <div className="mt-10 grid gap-5 md:grid-cols-2">
            {offers.map((offer) => <PriceCard key={offer.key} offer={offer} featured={offer.key === 'websiteChatbotVoice'} />)}
          </div>
          <p className="mt-5 text-xs leading-5 text-ash">
            Special pricing ends 31 December 2026. Standard pricing applies from 1 January 2027. We do not sell websites on their own.
          </p>
        </Container>
      </Section>

      <Section>
        <Container>
          <div className="rounded-3xl border border-gold/20 bg-gold/[0.05] p-6 md:p-8">
            <SectionHeading eyebrow="Early clients" title="Setup waived for the first five trades businesses." description="The setup waiver is limited to the first five trades businesses and ends 31 December 2026. Monthly pricing stays as shown above." />
            <div className="mt-6">
              <ButtonLink to="/contact">Ask about the setup waiver</ButtonLink>
            </div>
          </div>
          <p className="mt-6 text-sm leading-6 text-ash">
            All prices are in AUD. GST is confirmed in your quote. The final price is confirmed once we agree the details.
          </p>
        </Container>
      </Section>

      <Section className="border-y border-white/[0.04] bg-ink-light/40">
        <Container>
          <SectionHeading eyebrow="Questions" title="Pricing questions." />
          <div className="mt-8"><FAQList items={pricingFaqs} /></div>
        </Container>
      </Section>

      <CTASection
        title="Get a quote you can compare."
        description="We will break down setup, monthly, minutes, connections and support so you can compare it with your current setup."
        primaryTo="/contact"
        primaryLabel="Book a call"
        secondaryTo="/demo"
        secondaryLabel="Hear the demo"
      />
    </>
  );
}

export function DemoPage() {
  const hasPhone = Boolean(siteConfig.demoPhone);
  usePageMeta({
    title: 'Hear an AI Receptionist Answer a Real Call – CloudNext',
    description: 'Call the live demo number and hear how an AI receptionist answers, captures details and books an appointment.',
    path: '/demo',
    jsonLd: [organizationSchema(), serviceSchema({ name: 'AI Receptionist Demo', serviceType: 'AI receptionist live demo', path: '/demo', description: 'Live demo of the CloudNext AI receptionist.' })],
  });

  return (
    <>
      <PageHero
        eyebrow="Live demo"
        title={<>Call it. Try to <span className="text-gold-gradient">break it.</span></>}
        description="The fastest way to judge an AI receptionist is to call it yourself. Ask for a quote, book a job, ask for a human, or ask something it should not answer."
        breadcrumbs={[{ name: 'Home', to: '/' }, { name: 'Demo' }]}
      >
        {hasPhone ? (
          <div className="flex flex-col gap-3 sm:flex-row">
            <ButtonLink href={`tel:${siteConfig.demoPhone.replace(/\s+/g, '')}`}>Call {siteConfig.demoPhone}</ButtonLink>
            <ButtonLink to="/contact" variant="secondary">Book a call</ButtonLink>
          </div>
        ) : (
          <div className="flex flex-col gap-3 sm:flex-row">
            <ButtonLink to="/contact">Book a call</ButtonLink>
            <ButtonLink to="/ai-receptionist" variant="secondary">See the service</ButtonLink>
          </div>
        )}
      </PageHero>

      <Section>
        <Container>
          <div className="grid gap-10 lg:grid-cols-2">
            <div>
              <SectionHeading eyebrow="What to try" title="A five-minute test." />
              <div className="mt-6 space-y-3">
                {demoSteps.map((step, index) => (
                  <div key={step} className="flex gap-4 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
                    <span className="font-mono-accent text-sm text-gold-light">0{index + 1}</span>
                    <p className="text-sm leading-6 text-dust">{step}</p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <SectionHeading eyebrow="What happens next" title="A summary, a booking and a message when it can’t answer." />
              <FeatureList
                className="mt-6"
                items={[
                  'The caller details and a summary are sent to the business.',
                  'A booking goes into your calendar, if it’s connected.',
                  'A transfer or message is used when the AI cannot help.',
                  'Emergency calls are told to contact 000 and are passed to a person.',
                  'See our AI and data policy for how call data is handled.',
                ]}
              />
              <div className="mt-6 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 text-sm leading-6 text-dust">
                {hasPhone ? 'The demo line is a sample trade business. Do not use it for a real emergency.' : 'The live demo line is being connected. Book a call and we will contact you when it is ready.'}
              </div>
            </div>
          </div>
        </Container>
      </Section>

      <Section className="border-y border-white/[0.04] bg-ink-light/40">
        <Container>
          <DemoCallout />
        </Container>
      </Section>

      <CTASection
        title="Want a demo built around your business?"
        description="We can set up a sample call handling plan for your trade, services and booking rules, then walk through it with you."
        primaryTo="/contact"
        primaryLabel="Book a call"
        secondaryTo="/pricing"
        secondaryLabel="See pricing"
      />
    </>
  );
}
